import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import api from "../../../../chat/api";

/**
 * Computes the new bbox for a move/resize gesture given the cursor delta.
 * Coords are in PDF/image-space (top-left origin) — converted from CSS px
 * via the gesture's `scale`. Normalizes if the user drags a corner past
 * its opposite, and clamps to the page bounds when `dims` is provided.
 */
const computeNewBbox = (gesture, e, dims) => {
  const dx = (e.clientX - gesture.startMouseX) / gesture.scale;
  const dy = (e.clientY - gesture.startMouseY) / gesture.scale;
  const [x0, y0, x1, y1] = gesture.startBbox;
  let nb;
  switch (gesture.kind) {
    case "move":
      nb = [x0 + dx, y0 + dy, x1 + dx, y1 + dy];
      break;
    case "resize-nw":
      nb = [x0 + dx, y0 + dy, x1, y1];
      break;
    case "resize-n":
      nb = [x0, y0 + dy, x1, y1];
      break;
    case "resize-ne":
      nb = [x0, y0 + dy, x1 + dx, y1];
      break;
    case "resize-e":
      nb = [x0, y0, x1 + dx, y1];
      break;
    case "resize-se":
      nb = [x0, y0, x1 + dx, y1 + dy];
      break;
    case "resize-s":
      nb = [x0, y0, x1, y1 + dy];
      break;
    case "resize-sw":
      nb = [x0 + dx, y0, x1, y1 + dy];
      break;
    case "resize-w":
      nb = [x0 + dx, y0, x1, y1];
      break;
    default:
      nb = [x0, y0, x1, y1];
  }
  const [nx0, nx1] = nb[0] <= nb[2] ? [nb[0], nb[2]] : [nb[2], nb[0]];
  const [ny0, ny1] = nb[1] <= nb[3] ? [nb[1], nb[3]] : [nb[3], nb[1]];
  let bbox = [nx0, ny0, nx1, ny1];
  if (dims) {
    bbox = [
      Math.max(0, bbox[0]),
      Math.max(0, bbox[1]),
      Math.min(dims.width, bbox[2]),
      Math.min(dims.height, bbox[3]),
    ];
  }
  return bbox;
};

/**
 * Move-or-resize gesture for an existing field's bbox. Click-without-drag
 * opens the editor; drag-and-release optimistically updates local state
 * then PATCHes /fields/<id>/. Rolls back if the PATCH fails.
 */
export default function useBboxGesture({
  jobId,
  job,
  setJob,
  pageDims,
  drawMode,
  isFinalizedView,
  creatingField,
  onClickWithoutDrag,
}) {
  const [bboxGesture, setBboxGesture] = useState(null);
  const jobRef = useRef(job);
  useEffect(() => {
    jobRef.current = job;
  }, [job]);

  const handleBboxMouseDown = useCallback(
    (e, field, kind, scale) => {
      if (drawMode || isFinalizedView || creatingField) return;
      // Still saving (optimistic temp id) — no move/resize until it's persisted.
      if (String(field.id).startsWith("temp_")) return;
      if (e.button !== 0) return;
      e.stopPropagation();
      e.preventDefault();
      setBboxGesture({
        fieldId: field.id,
        kind,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startBbox: [...field.bbox],
        currentBbox: [...field.bbox],
        scale,
        moved: false,
      });
    },
    [drawMode, isFinalizedView, creatingField]
  );

  const patchFieldGeometry = useCallback(
    async (fieldId, bbox, rollbackBbox) => {
      try {
        const res = await api.patch(
          `/api/v1/rfp/autopdf/jobs/${jobId}/fields/${fieldId}/`,
          { bbox, write_targets: [bbox] }
        );
        if (![200, 201].includes(res.status)) {
          throw new Error("Geometry update failed");
        }
        const updated = res.data || {};
        setJob((prev) => {
          if (!prev) return prev;
          const fields = (prev.fields || []).map((f) =>
            f.id === fieldId
              ? {
                  ...f,
                  bbox: updated.bbox || bbox,
                  write_targets: updated.write_targets || [bbox],
                }
              : f
          );
          return { ...prev, fields };
        });
      } catch (err) {
        console.error("Geometry update failed:", err);
        const msg =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to update geometry.";
        toast.error(msg);
        if (rollbackBbox) {
          setJob((prev) => {
            if (!prev) return prev;
            const fields = (prev.fields || []).map((f) =>
              f.id === fieldId
                ? {
                    ...f,
                    bbox: rollbackBbox,
                    write_targets: [rollbackBbox],
                  }
                : f
            );
            return { ...prev, fields };
          });
        }
      }
    },
    [jobId, setJob]
  );

  // Global mouse listeners while a move/resize gesture is active.
  useEffect(() => {
    if (!bboxGesture) return;
    const handleMove = (e) => {
      setBboxGesture((prev) => {
        if (!prev) return prev;
        const field = jobRef.current?.fields?.find(
          (f) => f.id === prev.fieldId
        );
        const dims = field ? pageDims[field.page] : null;
        const newBbox = computeNewBbox(prev, e, dims);
        const dxAbs = Math.abs(e.clientX - prev.startMouseX);
        const dyAbs = Math.abs(e.clientY - prev.startMouseY);
        const moved = prev.moved || dxAbs > 3 || dyAbs > 3;
        return { ...prev, currentBbox: newBbox, moved };
      });
    };
    const handleUp = () => {
      setBboxGesture((prev) => {
        if (!prev) return null;
        const field = jobRef.current?.fields?.find(
          (f) => f.id === prev.fieldId
        );
        if (!prev.moved) {
          if (field) onClickWithoutDrag?.(field);
        } else if (field) {
          // Optimistic update so the bbox doesn't snap back while the
          // PATCH is in flight; patchFieldGeometry rolls back on failure.
          const newBbox = prev.currentBbox;
          const rollbackBbox = prev.startBbox;
          setJob((j) => {
            if (!j) return j;
            const fields = (j.fields || []).map((f) =>
              f.id === field.id
                ? { ...f, bbox: newBbox, write_targets: [newBbox] }
                : f
            );
            return { ...j, fields };
          });
          patchFieldGeometry(field.id, newBbox, rollbackBbox);
        }
        return null;
      });
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bboxGesture != null, pageDims]);

  return {
    bboxGesture,
    handleBboxMouseDown,
  };
}
